// ============================================================================
// FSA ADJUSTMENT JOURNAL PDF PREVIEW MODAL
// ============================================================================
// Engagement-wide adjustment-journal bundle PDF, with a mode toggle between:
//   • Working Papers    — full audit detail (every status, ledger, source, narration)
//   • Client Schedule   — POSTED only, simplified per-line view for the client
//
// One template, two layouts — backend renders both from the same data
// (FsaAdjustmentJournalPdfService). Mirrors FsaTemplatePdfPreviewModal's
// blob-iframe pattern: server returns PDF bytes → URL.createObjectURL →
// iframe → browser-native Save / Print.

import React, { useCallback, useEffect, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { fsaAdjustmentJournalService } from 'api/services/fsa/fsaAdjustmentJournalService';
import notificationService from 'services/notificationService';
import type { ID } from 'api/types';

export type JePdfMode = 'working-papers' | 'client-schedule';

interface Props {
  isOpen: boolean;
  engagementId: ID | null;
  /** Pre-select a mode when opening. Defaults to 'working-papers'. */
  initialMode?: JePdfMode;
  /** Optional engagement-context strip shown in the modal title. */
  engagementLabel?: string | null;
  onClose: () => void;
}

const FsaAdjustmentJournalPdfPreviewModal: React.FC<Props> = ({
  isOpen,
  engagementId,
  initialMode = 'working-papers',
  engagementLabel,
  onClose,
}) => {
  const [mode, setMode] = useState<JePdfMode>(initialMode);
  const [loading, setLoading] = useState(false);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Reset mode whenever the modal is opened — keeps the chosen mode
  // sticky during a single open but doesn't leak across sessions.
  useEffect(() => {
    if (isOpen) setMode(initialMode);
  }, [isOpen, initialMode]);

  // Fetch (or re-fetch) the blob whenever the modal is open + mode
  // changes. Mode flips re-issue a fresh GET; the previous blob URL is
  // revoked in the cleanup before the new one is set.
  useEffect(() => {
    let revoke: string | null = null;
    let cancelled = false;
    (async () => {
      if (!isOpen || !engagementId) return;
      setLoading(true);
      setError(null);
      setBlobUrl(null);
      try {
        const blob = await fsaAdjustmentJournalService.getBundlePdfBlob(engagementId, mode);
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        revoke = url;
        setBlobUrl(url);
      } catch (err: any) {
        if (cancelled) return;
        const message =
          err?.response?.data?.message ||
          err?.message ||
          'Failed to load PDF.';
        setError(message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [isOpen, engagementId, mode]);

  const downloadName = `FSA-Adjustment-Journals-${
    mode === 'client-schedule' ? 'Client-Schedule' : 'Working-Papers'
  }-${new Date().toISOString().slice(0, 10)}.pdf`;

  const handleDownload = useCallback(() => {
    if (!blobUrl) return;
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = downloadName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    notificationService.toast.success('Download started.');
  }, [blobUrl, downloadName]);

  const handlePrint = useCallback(() => {
    if (!blobUrl) return;
    const opened = window.open(blobUrl, '_blank');
    if (!opened) {
      notificationService.toast.warning(
        'Pop-up blocked. Allow pop-ups from this site so the PDF can open in a new tab.',
        6000,
      );
    }
  }, [blobUrl]);

  if (!isOpen) return null;

  const modeLabel = mode === 'client-schedule' ? 'Client Schedule' : 'Working Papers';

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div
        className="modal-container modal-xl"
        style={{ display: 'flex', flexDirection: 'column', maxHeight: '90vh' }}
      >
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0, flex: 1 }}>
            <IconSystem.print size={16} />
            <h2
              className="modal-title"
              style={{ margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              Adjustment Journals · {modeLabel}
              {engagementLabel && (
                <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginLeft: '0.5rem', fontWeight: 500 }}>
                  · {engagementLabel}
                </span>
              )}
            </h2>
          </div>
          <button className="modal-close" onClick={onClose}>
            <IconSystem.x size={20} />
          </button>
        </div>

        {/* Mode toggle strip — sits under the header so changing it
            re-fetches inline without dismissing the modal. */}
        <div style={modeStripStyle}>
          <button
            type="button"
            style={mode === 'working-papers' ? modePillActiveStyle : modePillStyle}
            onClick={() => setMode('working-papers')}
            disabled={loading}
          >
            Working Papers
            <span style={modePillHintStyle}>full audit detail · all statuses</span>
          </button>
          <button
            type="button"
            style={mode === 'client-schedule' ? modePillActiveStyle : modePillStyle}
            onClick={() => setMode('client-schedule')}
            disabled={loading}
          >
            Client Schedule
            <span style={modePillHintStyle}>posted only · simplified for client</span>
          </button>
        </div>

        <div
          className="modal-content"
          style={{
            flex: 1,
            padding: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            background: 'var(--bg-secondary)',
          }}
        >
          {loading && (
            <div style={spinnerWrapStyle}>
              <div style={spinnerStyle} />
              <span style={spinnerLabelStyle}>Rendering PDF…</span>
            </div>
          )}

          {error && !loading && (
            <div style={errorWrapStyle}>
              <IconSystem.alert size={28} />
              <div style={{ color: 'var(--text-primary)', fontWeight: 600 }}>Could not load PDF</div>
              <div style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>{error}</div>
            </div>
          )}

          {blobUrl && !loading && !error && (
            <iframe
              src={`${blobUrl}#toolbar=1&navpanes=0`}
              title="FSA Adjustment Journals PDF"
              style={{ flex: 1, width: '100%', height: '100%', border: 'none', minHeight: '60vh' }}
            />
          )}
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
          <button
            className="btn btn-secondary"
            onClick={handlePrint}
            disabled={loading || !blobUrl || !!error}
            title="Open in a new tab — use the browser's Print option from there"
          >
            <IconSystem.print size={16} />
            Open / Print
          </button>
          <button
            className="btn btn-primary"
            onClick={handleDownload}
            disabled={loading || !blobUrl || !!error}
          >
            <IconSystem.download size={16} />
            Download PDF
          </button>
        </div>
      </div>
    </>
  );
};

// ---------------------------------------------------------------------------
// styles
// ---------------------------------------------------------------------------

const modeStripStyle: React.CSSProperties = {
  display: 'flex',
  gap: 8,
  padding: '0.5rem 1rem',
  borderBottom: '1px solid var(--border-color)',
  background: 'var(--bg-primary)',
};
const modePillStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 2,
  padding: '6px 14px',
  border: '1px solid var(--form-border-color)',
  borderRadius: 6,
  background: 'transparent',
  color: 'var(--text-primary)',
  cursor: 'pointer',
  fontSize: '0.8125rem',
  fontWeight: 600,
};
const modePillActiveStyle: React.CSSProperties = {
  ...modePillStyle,
  background: 'var(--primary-color, #2563eb)',
  color: 'white',
  borderColor: 'var(--primary-color, #2563eb)',
};
const modePillHintStyle: React.CSSProperties = {
  fontSize: '0.65rem',
  fontWeight: 400,
  opacity: 0.85,
};

const spinnerWrapStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.75rem',
};
const spinnerStyle: React.CSSProperties = {
  width: '2rem',
  height: '2rem',
  border: '3px solid var(--border-color)',
  borderTopColor: '#2563eb',
  borderRadius: '50%',
  animation: 'spin 0.8s linear infinite',
};
const spinnerLabelStyle: React.CSSProperties = {
  fontSize: '0.85rem',
  color: 'var(--text-secondary)',
  fontWeight: 500,
};

const errorWrapStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.5rem',
  padding: '2rem',
  textAlign: 'center',
};

export default FsaAdjustmentJournalPdfPreviewModal;
